/**
 * useCoaches — fetches the list of coaches grouped by type.
 *
 * Staff + masterclass come from the dedicated `coaches` table (which
 * can hold coaches regardless of whether they have auth accounts).
 * Community coaches are authenticated users who've uploaded at least
 * one drill — pulled from the drills table + profiles, and unioned in
 * unless they're already listed in `coaches`.
 */
import { useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";
import { fetchDisplayProfiles } from "../utils/profileDisplay";

const cache = {};

function mapCoachRow(row) {
  return {
    id: row.id,
    name: row.name || "Coach",
    type: row.type || "community",
    isVerified: !!row.is_verified,
    avatarUrl: row.avatar_url || null,
  };
}

function mapProfileAsCoach(row) {
  const name = [row.first_name, row.last_name].filter(Boolean).join(" ") || "Coach";
  return {
    id: row.id,
    name,
    type: "community",
    isVerified: !!row.is_verified,
    avatarUrl: row.avatar_url || null,
  };
}

export function useCoaches() {
  const [state, setState] = useState(() => cache.v || { byId: {}, staff: [], masterclass: [], community: [], loading: !cache.v, error: null });

  useEffect(() => {
    let cancelled = false;
    if (cache.v && cache.ts && Date.now() - cache.ts < 300000) return;

    (async () => {
      try {
        // 1. Dedicated coaches table (staff + masterclass + any curated community coaches).
        const { data: coachRows, error: cErr } = await supabase
          .from("coaches")
          .select("id, name, type, is_verified, avatar_url");
        if (cErr) throw cErr;
        const all = (coachRows || []).map(mapCoachRow);

        // 2. Community fallback: users who've uploaded a drill but aren't
        //    in the coaches table. Pulled from drills + profiles.
        const { data: ownerRows } = await supabase
          .from("drills")
          .select("coach_id")
          .not("coach_id", "is", null);
        const ownerIds = [...new Set((ownerRows || []).map(r => r.coach_id))];
        const knownIds = new Set(all.map(c => c.id));
        const missingIds = ownerIds.filter(id => id && !knownIds.has(id));

        if (missingIds.length > 0) {
          // Through the public display read: a direct profiles select only
          // returns the viewer's own row, so every uploader read "Coach".
          const profiles = await fetchDisplayProfiles(missingIds);
          Object.values(profiles).forEach(p => all.push(mapProfileAsCoach(p)));
        }

        const byId = {};
        all.forEach(c => { byId[c.id] = c; });

        const next = {
          byId,
          staff:       all.filter(c => c.type === "staff").sort((a,b) => a.name.localeCompare(b.name)),
          masterclass: all.filter(c => c.type === "masterclass").sort((a,b) => a.name.localeCompare(b.name)),
          community:   all.filter(c => c.type === "community").sort((a,b) => a.name.localeCompare(b.name)),
          loading: false,
          error: null,
        };

        if (!cancelled) {
          cache.v = next; cache.ts = Date.now();
          setState(next);
        }
      } catch (e) {
        if (!cancelled) setState(s => ({ ...s, loading: false, error: e.message || String(e) }));
      }
    })();

    return () => { cancelled = true; };
  }, []);

  return state;
}

export function invalidateCoachesCache() {
  cache.v = null;
  cache.ts = null;
}
